import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  Button,
  Alert,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Chip,
  LinearProgress,
  Modal,
  ModalDialog,
  ModalClose,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/joy";
import {
  DotsSixVertical as DragIcon,
  CheckCircle as CheckIcon,
  Clock as PendingIcon,
  User as PersonIcon,
  Info as InfoIcon,
  Copy as CopyIcon,
  LinkSimple as LinkIcon,
} from "@phosphor-icons/react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { api } from "../../../lib/api";
import { getApiErrorMessage } from "../../../lib/apiError";

interface Recipient {
  email: string;
  name: string;
  role: string;
  signing_order: number;
  signed?: boolean;
  signed_at?: string;
}

interface SigningProgress {
  total_signers: number;
  signed_count: number;
  is_complete: boolean;
  next_signer?: Recipient;
  progress_percentage: number;
}

interface SigningOrderManagerProps {
  contractId: number;
  contractStatus?: string;
  initialUseSigningOrder?: boolean;
  initialRecipients?: Recipient[];
  onUpdate?: (data: any) => void;
  isReadOnly?: boolean;
}

export const SigningOrderManager: React.FC<SigningOrderManagerProps> = ({
  contractId,
  contractStatus,
  initialUseSigningOrder = true,
  initialRecipients = [],
  onUpdate,
  isReadOnly: requestedReadOnly = false,
}) => {
  const isReadOnly =
    requestedReadOnly ||
    (contractStatus !== undefined && contractStatus !== "draft");
  const [useSigningOrder, setUseSigningOrder] = useState(
    initialUseSigningOrder,
  );
  const [recipients, setRecipients] = useState<Recipient[]>(initialRecipients);
  const [signingProgress, setSigningProgress] =
    useState<SigningProgress | null>(null);
  const [signingLinks, setSigningLinks] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  // Fetch current signing progress
  useEffect(() => {
    fetchSigningProgress();
  }, [contractId]);

  // Fetch signing links when contract is sent (not draft)
  useEffect(() => {
    if (contractStatus && contractStatus !== "draft") {
      fetchSigningLinks();
    }
  }, [contractId, contractStatus]);

  // Update recipients when props change
  useEffect(() => {
    if (initialRecipients.length > 0) {
      setRecipients(initialRecipients);
    }
  }, [initialRecipients]);

  const fetchSigningProgress = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/api/crm/contracts/${contractId}/signing_progress/`,
      );
      const data = response.data as any;
      setSigningProgress(data.signing_progress);
      setUseSigningOrder(data.use_signing_order);
      // This endpoint is the authoritative list. Without adopting it the panel can
      // show "no recipients" for a contract that has them - and then offer an
      // Initialize button the server rejects with "already has recipients".
      if (Array.isArray(data.recipients)) {
        setRecipients(data.recipients);
      }
    } catch (err: any) {
      console.error("Error fetching signing progress:", err);
      setError(getApiErrorMessage(err, "Failed to load signing progress"));
    } finally {
      setLoading(false);
    }
  };

  const fetchSigningLinks = async () => {
    console.log("Fetching signing links for contract", contractId);
    try {
      const response = await api.get(
        `/api/crm/contracts/${contractId}/signing_links/`,
      );
      const data = response.data as any;
      const linkMap: Record<string, string> = {};

      // The response might be an object with recipients array or the links directly
      const links = data.recipients || data.links || data;

      // Handle both array and object formats
      if (Array.isArray(links)) {
        // Build a map of email to signing URL
        links.forEach((link: any) => {
          if (link.email && link.url) {
            linkMap[link.email] = link.url;
          }
        });
      } else if (typeof links === "object") {
        // If it's already a map of email -> url
        Object.entries(links).forEach(([email, url]) => {
          if (email && url) {
            linkMap[email] = url as string;
          }
        });
      }

      setSigningLinks(linkMap);
    } catch (err: any) {
      console.error("Error fetching signing links:", err);
      // Don't show error for this as it's supplementary info
    }
  };

  const copyToClipboard = (text: string, email: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedEmail(email);
        setTimeout(() => setCopiedEmail(null), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
      });
  };

  const handleToggleSigningOrder = async (enabled: boolean) => {
    if (isReadOnly) return;

    try {
      setSaving(true);
      setError(null);

      const response = await api.patch(
        `/api/crm/contracts/${contractId}/update_signing_order/`,
        {
          use_signing_order: enabled,
        },
      );

      setUseSigningOrder(enabled);
      setSigningProgress((response.data as any).signing_progress);

      if (onUpdate) {
        onUpdate(response.data as any);
      }
    } catch (err: any) {
      console.error("Error updating signing order:", err);
      setError(getApiErrorMessage(err, "Failed to update signing order"));
    } finally {
      setSaving(false);
    }
  };

  const handleInitializeRecipients = async () => {
    if (isReadOnly) return;

    try {
      setSaving(true);
      setError(null);

      const response = await api.post(
        `/api/crm/contracts/${contractId}/initialize_recipients/`,
        {},
      );

      setRecipients((response.data as any).recipients);
      setSigningProgress((response.data as any).signing_progress);
      setUseSigningOrder((response.data as any).use_signing_order);

      if (onUpdate) {
        onUpdate(response.data as any);
      }
    } catch (err: any) {
      console.error("Error initializing recipients:", err);
      setError(getApiErrorMessage(err, "Failed to initialize recipients"));
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination || isReadOnly) return;

    const newRecipients = Array.from(recipients);
    const [reorderedItem] = newRecipients.splice(result.source.index, 1);
    newRecipients.splice(result.destination.index, 0, reorderedItem);

    // Update signing order
    const updatedRecipients = newRecipients.map((recipient, index) => ({
      ...recipient,
      signing_order: index + 1,
    }));

    setRecipients(updatedRecipients);

    try {
      setSaving(true);
      setError(null);

      const response = await api.patch(
        `/api/crm/contracts/${contractId}/update_signing_order/`,
        {
          recipients: updatedRecipients,
          use_signing_order: useSigningOrder,
        },
      );

      setSigningProgress((response.data as any).signing_progress);

      if (onUpdate) {
        onUpdate(response.data as any);
      }
    } catch (err: any) {
      console.error("Error updating signing order:", err);
      setError(getApiErrorMessage(err, "Failed to update signing order"));
      // Revert on error
      setRecipients(recipients);
    } finally {
      setSaving(false);
    }
  };

  const getRecipientStatus = (recipient: Recipient) => {
    if (recipient.signed) {
      return { color: "success" as const, icon: <CheckIcon />, text: "Signed" };
    }

    if (!useSigningOrder) {
      return {
        color: "neutral" as const,
        icon: <PendingIcon />,
        text: "Pending",
      };
    }

    const isNextSigner =
      signingProgress?.next_signer?.email === recipient.email;
    if (isNextSigner) {
      return {
        color: "warning" as const,
        icon: <PendingIcon />,
        text: "Current Signer",
      };
    }

    return {
      color: "neutral" as const,
      icon: <PendingIcon />,
      text: "Waiting",
    };
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CircularProgress size="sm" />
            <Typography>Loading signing order...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography level="h4" startDecorator={<PersonIcon />}>
              Signing Order
            </Typography>
            <Tooltip title="Learn about signing order">
              <IconButton size="sm" onClick={() => setShowInfo(true)}>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Signing Progress */}
          {signingProgress && (
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography level="body-sm" color="neutral">
                  Progress: {signingProgress.signed_count} of{" "}
                  {signingProgress.total_signers} signed
                </Typography>
                <Typography level="body-sm" color="neutral">
                  {Math.round(signingProgress.progress_percentage)}%
                </Typography>
              </Box>
              <LinearProgress
                determinate
                value={signingProgress.progress_percentage}
                color={signingProgress.is_complete ? "success" : "primary"}
                sx={{ mb: 2 }}
              />

              {signingProgress.next_signer && !signingProgress.is_complete && (
                <Alert color="primary" size="sm">
                  Next signer: {signingProgress.next_signer.name} (
                  {signingProgress.next_signer.email})
                </Alert>
              )}

              {signingProgress.is_complete && (
                <Alert color="success" size="sm">
                  All signers have completed signing!
                </Alert>
              )}
            </Box>
          )}

          {/* Sequential Signing Toggle */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
              p: 2,
              bgcolor: "background.level1",
              borderRadius: "sm",
            }}
          >
            <Box>
              <Typography level="title-sm">Sequential Signing</Typography>
              <Typography level="body-xs" color="neutral">
                {useSigningOrder
                  ? "Recipients must sign in the specified order"
                  : "Recipients can sign in any order"}
              </Typography>
            </Box>
            <Switch
              checked={useSigningOrder}
              onChange={(e) => handleToggleSigningOrder(e.target.checked)}
              disabled={isReadOnly || saving}
            />
          </Box>

          {/* Recipients List */}
          {recipients.length > 0 && (
            <Box>
              <Typography level="title-sm" sx={{ mb: 2 }}>
                Recipients ({recipients.length})
              </Typography>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="recipients">
                  {(provided) => (
                    <List
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{ gap: 1 }}
                    >
                      {recipients.map((recipient, index) => {
                        const status = getRecipientStatus(recipient);

                        return (
                          <Draggable
                            key={recipient.email}
                            draggableId={recipient.email}
                            index={index}
                            isDragDisabled={isReadOnly || !useSigningOrder}
                          >
                            {(provided, snapshot) => (
                              <ListItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  bgcolor: snapshot.isDragging
                                    ? "background.level2"
                                    : "background.surface",
                                  border: "1px solid",
                                  borderColor: snapshot.isDragging
                                    ? "primary.500"
                                    : "divider",
                                  borderRadius: "sm",
                                  p: 2,
                                  cursor:
                                    useSigningOrder && !isReadOnly
                                      ? "grab"
                                      : "default",
                                }}
                              >
                                <ListItemDecorator>
                                  {useSigningOrder && !isReadOnly && (
                                    <Box {...provided.dragHandleProps}>
                                      <DragIcon color="var(--joy-palette-text-secondary)" />
                                    </Box>
                                  )}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 32,
                                      height: 32,
                                      bgcolor:
                                        status.color === "success"
                                          ? "success.100"
                                          : status.color === "warning"
                                            ? "warning.100"
                                            : "neutral.100",
                                      borderRadius: "50%",
                                      ml:
                                        useSigningOrder && !isReadOnly ? 0 : 2,
                                    }}
                                  >
                                    <Typography
                                      level="title-sm"
                                      color={status.color}
                                    >
                                      {recipient.signing_order}
                                    </Typography>
                                  </Box>
                                </ListItemDecorator>

                                <ListItemContent>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Typography level="title-sm">
                                        {recipient.name}
                                      </Typography>
                                      <Typography
                                        level="body-xs"
                                        color="neutral"
                                      >
                                        {recipient.email}
                                      </Typography>
                                      <Typography
                                        level="body-xs"
                                        color="neutral"
                                        sx={{ textTransform: "capitalize" }}
                                      >
                                        {recipient.role}
                                      </Typography>
                                      {recipient.signed_at && (
                                        <Typography
                                          level="body-xs"
                                          color="neutral"
                                        >
                                          Signed:{" "}
                                          {formatDate(recipient.signed_at)}
                                        </Typography>
                                      )}

                                      {/* Show signing link for unsigned recipients when contract is sent */}
                                      {!recipient.signed &&
                                        contractStatus &&
                                        contractStatus !== "draft" &&
                                        signingLinks[recipient.email] && (
                                          <Box
                                            sx={{
                                              mt: 1,
                                              display: "flex",
                                              flexDirection: {
                                                xs: "column",
                                                sm: "row",
                                              },
                                              alignItems: {
                                                xs: "flex-start",
                                                sm: "center",
                                              },
                                              gap: 0.5,
                                            }}
                                          >
                                            <LinkIcon
                                              size={14}
                                              color="var(--joy-palette-primary-500)"
                                              style={{ flexShrink: 0 }}
                                            />
                                            <Typography
                                              level="body-xs"
                                              sx={{
                                                fontFamily: "monospace",
                                                fontSize: "0.7rem",
                                                bgcolor: "background.level1",
                                                px: 0.75,
                                                py: 0.25,
                                                borderRadius: "xs",
                                                wordBreak: "break-all",
                                                flex: 1,
                                                maxWidth: "100%",
                                                overflow: "hidden",
                                                color:
                                                  "var(--brand-text, #7013f5)",
                                                cursor: "pointer",
                                                lineHeight: 1.4,
                                                display: "block",
                                                "&:hover": {
                                                  bgcolor: "primary.50",
                                                },
                                              }}
                                              onClick={() =>
                                                copyToClipboard(
                                                  signingLinks[recipient.email],
                                                  recipient.email,
                                                )
                                              }
                                              title={
                                                signingLinks[recipient.email]
                                              }
                                            >
                                              {signingLinks[recipient.email]}
                                            </Typography>
                                            <Tooltip
                                              title={
                                                copiedEmail === recipient.email
                                                  ? "Copied!"
                                                  : "Copy link"
                                              }
                                            >
                                              <IconButton
                                                size="sm"
                                                variant="plain"
                                                onClick={() =>
                                                  copyToClipboard(
                                                    signingLinks[
                                                      recipient.email
                                                    ],
                                                    recipient.email,
                                                  )
                                                }
                                                sx={{
                                                  minWidth: "auto",
                                                  color:
                                                    copiedEmail ===
                                                    recipient.email
                                                      ? "success.500"
                                                      : "neutral.500",
                                                }}
                                              >
                                                <CopyIcon size={14} />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        )}
                                    </Box>

                                    <Chip
                                      color={status.color}
                                      variant="soft"
                                      size="sm"
                                      startDecorator={status.icon}
                                      sx={{ ml: 1 }}
                                    >
                                      {status.text}
                                    </Chip>
                                  </Box>
                                </ListItemContent>
                              </ListItem>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
          )}

          {recipients.length === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Alert color="neutral">
                No recipients have been added to this contract yet.
              </Alert>
              {!isReadOnly && (
                <Button
                  color="primary"
                  variant="soft"
                  onClick={handleInitializeRecipients}
                  disabled={saving}
                  loading={saving}
                  startDecorator={<PersonIcon />}
                >
                  Initialize Recipients from Contract Details
                </Button>
              )}
            </Box>
          )}

          {saving && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size="sm" />
              <Typography level="body-sm" color="neutral">
                Saving changes...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Info Modal */}
      <Modal open={showInfo} onClose={() => setShowInfo(false)}>
        <ModalDialog sx={{ maxWidth: 500 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            About Signing Order
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography level="body-sm">
              <strong>Sequential Signing:</strong> When enabled, recipients must
              sign in the exact order specified. Each signer will only receive
              their invitation after the previous signer completes their
              signature.
            </Typography>

            <Typography level="body-sm">
              <strong>Parallel Signing:</strong> When disabled, all recipients
              receive invitations immediately and can sign in any order they
              choose.
            </Typography>

            <Typography level="body-sm">
              <strong>Reordering:</strong> You can drag and drop recipients to
              change the signing order. This is only available for draft
              contracts.
            </Typography>

            <Alert color="primary" size="sm">
              The contract will only be marked as complete after ALL recipients
              have signed, regardless of the signing order setting.
            </Alert>
          </Box>
        </ModalDialog>
      </Modal>
    </>
  );
};
